import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Link, Typography, Stack } from "@mui/material";
import { EGButton } from "../../../../../../MainComponents/Button/EGButton/EGButton";
import { EGInput } from "../../../../../../MainComponents/Input/EGInput";
import { useSnackbar } from "notistack";
import useUnsavedChangesWarning from "../../../../../../../hooks/useUnsavedChangesWarning";
import { useReportContext } from "../../../../../ReportContext/useReportContext";
import { createMeasure } from "../../../../../../../apis";
import { IconHeader } from "../../../../../../Icon/IconHader/IconHeader";
import { UnsavedChanges } from "../../../../../../Modal/Confirmation/UnsavedChanges/UnsavedChanges";
import { useReportId } from "../../../../../../../context/report/ReportContext";
import { CatalogueMultiSelect } from "../../../../../../MainComponents/Catalogues/CatalogueMultiSelect";
import { useActionsCatalogue } from "../../../../../../../hooks/useActionsCatalogue";
import {
  ACTION_TYPES,
  splitProposals,
} from "../../../../../../../helpers/correctiveActions";
import { InvestigationProposals } from "../InvestigationProposals/InvestigationProposals";

export const MeasuresForm = ({
  closeForm,
  reportRender,
  getAllMeasures,
  investigationList = [],
}) => {
  const [t] = useTranslation("report");
  const id = useReportId();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { measureOptions } = useReportContext();

  // Mismo origen de opciones que la conclusion de la investigacion: catalogo
  // unificado si el tenant lo tiene, y si no, las opciones hardcodeadas.
  const { catalogue, isOwn, rootPath, isLoading: isCatalogueLoading } =
    useActionsCatalogue(ACTION_TYPES.MEASURE);

  const [addDetails, setAddDetails] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [actions, setActions] = useState([]);
  const [another, setAnother] = useState([]);
  const [comment, setComment] = useState("");
  // Propuestas de los investigadores, ya marcadas con su investigacion
  const [proposals, setProposals] = useState([]);
  const [triedToSave, setTriedToSave] = useState(false);
  // Show unsaved changes confirmation modal
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  // Detecta si el usuario capturó algo en el formulario
  const hasChanges =
    actions.length > 0 ||
    another.length > 0 ||
    !!comment ||
    proposals.some((proposal) => proposal.confirmed === false);
  // Prevent saving before leaving
  useUnsavedChangesWarning(hasChanges);

  // Las opciones planas de respaldo excluyen "another": el texto libre ya tiene
  // su propio campo dentro del selector.
  const fallbackOptions = measureOptions.filter(
    (option) => option.value !== "another"
  );

  // onSubmit
  const onSubmitMesure = async (e) => {
    e.preventDefault();
    setTriedToSave(true);
    if (hasMissingReason) {
      enqueueSnackbar(t("Resolution.discard_reason_required"), {
        variant: "error",
      });
      return;
    }

    const cleanAnother = another.map((text) => text.trim()).filter(Boolean);
    const measureDataSend = {
      report_identifier: Number(id),
      type: ACTION_TYPES.MEASURE,
      // Lo confirmado de la investigacion se guarda junto a lo que el resolutor
      // agrego por su cuenta: para el registro es una sola lista de medidas.
      measures: [...confirmed, ...actions],
      // Lo capturado a mano no lleva marca de investigacion; lo confirmado si.
      another: [...proposalAnother, ...cleanAnother.map((text) => ({ text }))],
      discarded,
      comment,
    };
    setIsLoading(true);

    const sending = enqueueSnackbar(`${t("Resolution.sending")}...`, {
      variant: "info",
      persist: true,
    });

    try {
      await createMeasure(measureDataSend).then((resp) => {
        if (resp.status === 201) {
          setIsLoading(false);
          resetValues();
          closeForm();
          reportRender();
          closeSnackbar(sending);
          getAllMeasures();
          enqueueSnackbar(t("Resolution.measure_added_successfully"), {
            variant: "success",
          });
        }
      });
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      closeSnackbar(sending);
    }
  };
  const resetValues = () => {
    setActions([]);
    setAnother([]);
    setComment("");
    setProposals([]);
    setTriedToSave(false);
  };

  // Limpia el formulario y lo cierra
  const resetAndClose = () => {
    resetValues();
    closeForm();
  };

  // Prevent saving before leaving
  const hdlCancel = () => {
    if (hasChanges) {
      setShowUnsavedModal(true);
      return;
    }
    resetAndClose();
  };

  // Confirmar salida descartando los cambios
  const hdlConfirmLeave = () => {
    setShowUnsavedModal(false);
    resetAndClose();
  };

  // Lo que se toma de la investigacion y lo que el resolutor decidio no aplicar
  const { confirmed, another: proposalAnother, discarded } =
    splitProposals(proposals);

  // Un descarte sin motivo deja el registro sin explicar por que no se aplico.
  const hasMissingReason = discarded.some((item) => !item.reason);

  // Validation info
  const isInvalid =
    (confirmed.length === 0 &&
      proposalAnother.length === 0 &&
      actions.length === 0 &&
      another.filter((text) => text.trim() !== "").length === 0) ||
    another.some((text) => text.trim() !== "" && text.trim().length < 3) ||
    hasMissingReason;

  return (
    <Box component="form">
      <IconHeader title={t("Resolution.corrective_preventive_measure")} />
      <Box my={1.5}>
        <InvestigationProposals
          investigations={investigationList}
          type={ACTION_TYPES.MEASURE}
          proposals={proposals}
          onProposalsChange={setProposals}
          showErrors={triedToSave}
        />

        <Typography variant="body2" fontWeight={600} color="secondary" mt={2}>
          {t("Resolution.add_on_your_own")}
        </Typography>

        <Box mt={1} />

        {!isCatalogueLoading && (
          <CatalogueMultiSelect
            label={`${t("Resolution.select_corrective_preventive_measure")}:`}
            placeholder={t("Resolution.select_corrective_preventive_measure")}
            catalogue={catalogue}
            isOwn={isOwn}
            rootPath={rootPath}
            options={fallbackOptions}
            value={actions}
            onChange={setActions}
            another={another}
            onAnotherChange={setAnother}
            freeTextLabel={t("Resolution.new_corrective_preventive_measure")}
            freeTextPlaceholder={t(
              "Resolution.example_implement_monthly_audits"
            )}
            addFreeTextTitle={t(
              "Resolution.enter_new_corrective_preventive_measure"
            )}
          />
        )}

        {hasChanges && !addDetails && (
          <Typography textAlign="end" my={0.33}>
            <Link
              underline="hover"
              onClick={() => {
                setAddDetails(true);
                setTimeout(() => {
                  window.scrollTo({
                    top: document.documentElement.scrollHeight,
                    behavior: "smooth",
                  });
                }, 100);
              }}
            >
              {`+ ${t("Resolution.add_comment")}`}
            </Link>
          </Typography>
        )}

        {addDetails && (
          <EGInput
            sx={{ mt: 1.75, mb: 0.75 }}
            label={`${t("Resolution.details")}: ${comment.length}/500`}
            placeholder={t("Resolution.enter_details_related_selected_measure")}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            multiline
            autoFocus
            inputProps={{ maxLength: 500 }}
            rows={3.5}
          />
        )}
      </Box>
      <Stack
        display="flex"
        alignItems="center"
        justifyContent="end"
        flexDirection="row"
        flexWrap={{ xs: "wrap", sm: "nowrap" }}
        spacing={2}
        mt={1.5}
        mb={1}
      >
        <EGButton
          label={t("Resolution.save")}
          onClick={onSubmitMesure}
          loading={isLoading}
          iconName={isInvalid ? "mark_check" : "mark_check_bold"}
          disabled={isInvalid}
          color={isInvalid ? "light" : "success"}
          iconColor={isInvalid ? "" : "white"}
          iconColorHover={isInvalid ? "" : "white"}
          labelColor={isInvalid ? "" : "white"}
        />
        <EGButton
          iconName="close_icon"
          iconSize={21}
          label={t("Resolution.cancel")}
          onClick={hdlCancel}
          loading={isLoading}
        />
      </Stack>

      <UnsavedChanges
        open={showUnsavedModal}
        onClose={() => setShowUnsavedModal(false)}
        onCancel={() => setShowUnsavedModal(false)}
        onConfirm={hdlConfirmLeave}
      />
    </Box>
  );
};
